import clsx from "clsx";
import { PRIMARY_BUTTON } from "@/constants";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PaginationComponent from "@/app/components/ui/Pagination";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useCategorySubCategoryMapping } from "@/app/hooks/api_hooks/Inventory/CategorySubCategoryMapping/useCategorySubCategoryMappingAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { FiEdit } from "react-icons/fi";
import { useEffect, useState } from "react";
import { TbCircleLetterC } from "react-icons/tb";
import { useNavigate } from "react-router";

const PageSize = 10;

const tableHeader = [{ item: "Categories" }, { item: "Sub Categories" }, { item: "Action" }];

const CategorySubCategoryMapping = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { NO_APPOINTMENTS } = useImageConstants();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const isProductListInProgess = useLoader("isInventoryProductListLoadingInProgess");
  const { execute: fetchCategoryMappingList } = useCategorySubCategoryMapping();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { categoryMappingList } = routeState.state as IInventoryState;
  const [expandedRows, setExpandedRows] = useState({});

  const toggleView = (categoryId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  useEffect(() => {
    fetchCategoryMappingList(currentPage, PageSize, searchQuery);
  }, [currentPage, searchQuery]);

  return (
    <>
      <div className="flex flex-col items-stretch gap-3 bg-white px-4 sm:px-6 lg:px-8 py-3 sm:flex-row sm:items-center">
        <div className="flex-auto min-w-0">
          <h1 className="font-semibold text-sm text-gray-900 leading-6">VMS Category-SubCategory Mapping</h1>
        </div>
        <div className="flex-none">
          <button
            type="button"
            onClick={() => navigate(getRouteKey("CATEGORY_MAPPING_ONBOARD", "url"))}
            className={`${PRIMARY_BUTTON} w-full sm:w-auto`}
          >
            Create New Mapping
          </button>
        </div>
      </div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 rounded-md mt-2 shadow-sm">
        <div>
          <InputWithAddOn
            onEnterKeyPress={(query: string) => setSearchQuery(query)}
            containerClassName="w-full sm:w-1/2 lg:w-1/3"
            addon={<TbCircleLetterC className="w-4 h-4" />}
            placeholder="Search by Category Name"
          />
        </div>
        <div className="mt-8 flow-root">
          {isProductListInProgess ? (
            <TableSkeleton />
          ) : categoryMappingList?.data?.length === 0 ? (
            <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Data Found." />
          ) : (
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader?.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {key?.item}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {categoryMappingList?.data?.map((cat, idx) => {
                            const isExpanded = expandedRows[cat?.category_id] || false;
                            const displayedSubCategories = isExpanded
                              ? cat?.sub_category_names
                              : cat?.sub_category_names?.slice(0, 4);
                            const isFirst = idx === 0;
                            const isLast = idx === categoryMappingList?.data?.length - 1;
                            const isSingle = categoryMappingList?.data?.length === 1;

                            return (
                              <tr
                                key={cat?.category_id}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 align-top text-xs font-normal",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  {cat?.category_name}
                                </td>
                                <td className="px-3 py-3 align-top text-xs font-normal">
                                  {displayedSubCategories?.map((subCategoryName, index) => (
                                    <p key={index}>{subCategoryName}</p>
                                  ))}
                                  {cat?.sub_category_names?.length > 4 && (
                                    <button
                                      type="button"
                                      onClick={() => toggleView(cat?.category_id)}
                                      className="text-blue-500"
                                    >
                                      {isExpanded ? "View Less" : "View More"}
                                    </button>
                                  )}
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 align-top text-xs font-normal cursor-pointer",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                  onClick={() => {
                                    navigate(`/app/inventory/category-mapping-onboard/${cat?.category_id}`);
                                  }}
                                >
                                  <FiEdit className="w-4 h-4 text-gray-500 hover:text-black" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {categoryMappingList?.item_count && searchQuery.trim() === "" && (
            <div className="flex justify-center items-center p-4 w-full">
              <PaginationComponent
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={categoryMappingList?.item_count}
                pageSize={PageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CategorySubCategoryMapping;
